// global $

import React, { Dispatch } from 'react'
import ReactDOMServer from 'react-dom/server'
import Leaflet from 'leaflet'
// @ts-ignore
import omnivore from '@mapbox/leaflet-omnivore'

import './GeographicalMap.scss'
import MapTooltip from './MapTooltip'

import { connect } from 'react-redux'
import {
  IMapLocation,
  IReduxAction,
  IReduxState
} from '../../../utils/ReduxStore'
import { resultForKey } from '../../../controllers/DataController'
import { compareResultKeys } from '../../../controllers/ElectionResultStore'
import IconLabel from '../../Helpers/IconLabel'
import U from '../../../utils/Utilities'

interface IStateProps {
  mapLocation: IMapLocation
  compareResultKey: string
  baseResultKey: string
}

interface IDispatchProps {
  setMapLocation: (mapLocation: IMapLocation) => void
}

interface IProps extends IStateProps, IDispatchProps {}

interface IState {
  showSeatChanges: boolean
  height: string
}

class GeographicalMap extends React.Component<IProps, IState> {
  protected mapRef: React.RefObject<HTMLDivElement>
  protected map?: Leaflet.Map
  protected ridingLayer?: Leaflet.GeoJSON

  public constructor(props: IProps) {
    super(props)

    this.state = {
      showSeatChanges: false,
      height: ''
    }

    this.mapRef = React.createRef()

    this.polystyle = this.polystyle.bind(this)
    this.tooltip = this.tooltip.bind(this)
  }

  protected polystyle = (feature: any): any => {
    const ridingId = feature.properties.FED_NUM
    const result = resultForKey(this.props.compareResultKey).resultForRiding(
      ridingId
    )
    const ridingClass = `riding-${ridingId}`

    let styleObj: { [key: string]: string | number } = {
      className: `${ridingClass}`,
      weight: 1,
      opacity: 0.5,
      color: 'white'
    }

    if (this.state.showSeatChanges) {
      const result2015 = resultForKey(this.props.baseResultKey).resultForRiding(
        ridingId
      )
      styleObj.fillColor =
        result.winner().party.key === result2015.winner().party.key
          ? '#999'
          : result.winner().party.rgbColor
      styleObj.fillOpacity =
        result.winner().party.key === result2015.winner().party.key
          ? 0.1
          : Math.min(0.4 + result.margin() * 2, 0.8)
    } else {
      const winnerColor = result.winner().party.rgbColor
      const fillOpacity = Math.min(0.4 + result.margin() * 2, 0.8)
      styleObj.fillColor = winnerColor
      styleObj.fillOpacity = fillOpacity
    }

    return styleObj
  }

  protected tooltip = (layer: any): string => {
    const ridingId = layer.feature.properties.FED_NUM
    const result = resultForKey(this.props.compareResultKey).resultForRiding(
      ridingId
    )
    const popover = (
      <MapTooltip result={result} baseResultKey={this.props.baseResultKey} />
    )
    return ReactDOMServer.renderToStaticMarkup(popover)
  }

  protected invalidateSize(): void {
    if (this.map && this.ridingLayer) {
      this.map.invalidateSize()
      this.ridingLayer.setStyle(this.polystyle)
    }
  }

  public componentDidUpdate(prevProps: IProps, prevState: IState): void {
    const heightChanged = prevState.height !== this.state.height
    const mapLocationSame = prevProps.mapLocation === this.props.mapLocation
    const baseKeyChanged = compareResultKeys(
      prevProps.baseResultKey,
      this.props.baseResultKey
    )
    const compareKeyChanged = compareResultKeys(
      prevProps.compareResultKey,
      this.props.compareResultKey
    )

    if (
      heightChanged ||
      mapLocationSame ||
      baseKeyChanged ||
      compareKeyChanged
    ) {
      this.invalidateSize()
    }
  }

  protected changeHeight(): void {
    let headerHeight = 0
    let comparisonHeight = 0

    const headerEl = document.getElementById('Header')
    if (headerEl) {
      headerHeight = headerEl.getBoundingClientRect().height
    }
    const comparisonEl = document.getElementById('Comparison')
    if (comparisonEl) {
      comparisonHeight = comparisonEl.getBoundingClientRect().height
    }

    const height = `calc(100vh - ${headerHeight}px - ${comparisonHeight}px - 1px)`

    this.setState({ height })
  }

  public componentDidMount(): void {
    window.addEventListener(`show-map`, this.invalidateSize.bind(this))
    window.addEventListener('resize', (): void => {
      this.changeHeight()
    })
    window.addEventListener('resize-map', (): void => {
      this.changeHeight()
    })

    const { lat, lng, zoom } = this.props.mapLocation
    const node = this.mapRef.current
    if (node) {
      this.map = Leaflet.map(node).setView([lat, lng], zoom)

      Leaflet.tileLayer(
        `https://api.mapbox.com/styles/v1/{id}/tiles/{z}/{x}/{y}?access_token={accessToken}`,
        {
          attribution:
            'Map data &copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors, <a href="https://creativecommons.org/licenses/by-sa/2.0/">CC-BY-SA</a>, Imagery © <a href="https://www.mapbox.com/">Mapbox</a>',
          maxZoom: 18,
          tileSize: 512,
          id: 'mapbox/light-v10',
          zoomOffset: -1,
          accessToken:
            'pk.eyJ1IjoiaGFuZ2xlciIsImEiOiJjazBwdm95ZWwwMjNwM21vYzNhYWJobngxIn0.Tqlm26lgjO9Iby50ls_zAg'
        }
      ).addTo(this.map)

      this.ridingLayer = Leaflet.geoJSON(undefined, { style: this.polystyle })

      const topoJSON = omnivore.topojson(
        './data/geo/final/canada-ed.topo.json',
        undefined,
        this.ridingLayer
      )

      topoJSON.addTo(this.map)

      this.ridingLayer.bindTooltip(this.tooltip, {
        sticky: true
      })

      this.map.on('zoomend', (): void => {
        if (this.map && this.ridingLayer) {
          const currentZoom = this.map.getZoom()
          this.ridingLayer.setStyle({ weight: currentZoom * 0.2 })
          // if (currentZoom < 10) {
          //   ridingLayer.setStyle({ weight: 1 })
          // } else {
          //   ridingLayer.setStyle({ weight: 5 })
          // }
        }
      })

      this.map.on('moveend', (): void => {
        if (this.map) {
          const { lat, lng } = this.map.getCenter()
          const zoom = this.map.getZoom()
          this.props.setMapLocation({ lat, lng, zoom })
        }
      })
    }

    this.changeHeight()
  }

  public render(): JSX.Element {
    const { height, showSeatChanges } = this.state

    return (
      <div className="GeographicalMap">
        <div id="GeographicalMap" ref={this.mapRef} style={{ height }} />
        {!compareResultKeys(
          this.props.compareResultKey,
          this.props.baseResultKey
        ) && (
          <button
            id="ToggleSeats"
            className="btn btn-secondary"
            onClick={(): void => {
              this.setState({ showSeatChanges: !showSeatChanges })
            }}
          >
            <span className={U.classIf(showSeatChanges, '', 'Hidden')}>
              <IconLabel icon="fas fa-award" label="Show winners" />
            </span>
            <span className={U.classIf(showSeatChanges, 'Hidden', '')}>
              <IconLabel icon="fas fa-chair" label="Show changed seats" />
            </span>
          </button>
        )}
      </div>
    )
  }
}

export const mapStateToProps = (state: IReduxState): IStateProps => ({
  baseResultKey: state.baseResultKey,
  compareResultKey: state.compareResultKey,
  mapLocation: state.mapLocation
})

const mapDispatchToProps = (
  dispatch: Dispatch<IReduxAction>
): IDispatchProps => ({
  setMapLocation: (mapLocation: IMapLocation): void => {
    dispatch({ type: 'SET_MAP_LOCATION', mapLocation })
  }
})

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(GeographicalMap)
